import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ZPayBridge } from "src/app/features/digital-payments/z-pay-bridge/z-pay-bridge.model";
import { ItemDataCatalogService } from "src/app/features/items/services/item-data-catalog.service";
import { Departments, Printers } from "tilby-models";

@Injectable()
export class GridCellValueGetterService {
    private readonly itemDataCatalogService = inject(ItemDataCatalogService);
    private readonly translateService = inject(TranslateService);
    

    private getDepartmentName(idFieldName: string) { 
        return (params: any) => {
            const id = params.data?.[idFieldName];
            if (!id) return '';

            const departmentList = this.itemDataCatalogService.getDataByKey('departments') ?? [];
            const foundDepartment = departmentList.find((d: Departments) => d.id === parseInt(id));

            return foundDepartment ? foundDepartment.name : id;
        }
    }

    private getDepartmentId(idFieldName: string, objFieldName: string) {
        return (params: any) => {
            return params.data?.[idFieldName]? String(params.data?.[idFieldName]) :
                params.data?.[objFieldName]?.id? String(params.data?.[objFieldName]?.id) : null;
        }
    }

    private getPrinterName = (params: any) => {
        const id = params.data?.printer_id;
        if (!id) return '';

        const printerList = this.itemDataCatalogService.getDataByKey('printers') ?? [];
        const foundPrinter = printerList.find((p: Printers) => p.id === parseInt(id));

        return foundPrinter ? foundPrinter.name : id;
    }

    private getTerminalName = (params: any) => {
        const id = params.data?.terminal_id;
        if (!id) return '';

        const terminalList = this.itemDataCatalogService.getDataByKey('z-pay-bridge') ?? [];
        const foundTerminal = terminalList.find((p: ZPayBridge.POS) => p.id === id);

        return foundTerminal ? foundTerminal.name : id;
    }

    private getSku = (params: any) => {
        // console.log("getSku ", params);
        return params.data?.sku? params.data.sku: '';
    }

    private getCategoryId = (params: any) => {
        return params.data?.category_id?.toString() || params.data?.category?.id?.toString() || null;
    }

    private getBarcodeList = (params: any) => {
        const barcodeList = params.data?.barcodes;
        return barcodeList?.filter((item: any) => item.barcode).map((item: any) => item.barcode).join(', ');
    }

    private getMovementType = (params: any) => {
        let movementType = this.translateService.instant('STOCK.TOPBAR_STOCK.' + params.data?.type?.toUpperCase());

        if(params.data?.type !== 'setup') {
            movementType += ' (' + this.translateService.instant('STOCK.CAUSES.' + (params.data?.load_cause || params.data?.unload_cause || '').toUpperCase()) + ')';
        }

        return movementType;
    }

    categories = new Map<string, Function>([
        ["department_name", this.getDepartmentName('department_id')],
        ["department2_name", this.getDepartmentName('department2_id')],
        ["department3_name", this.getDepartmentName('department3_id')],
        ["department4_name", this.getDepartmentName('department4_id')],
        ["department5_name", this.getDepartmentName('department5_id')],
        ["department6_name", this.getDepartmentName('department6_id')],
        ["department7_name", this.getDepartmentName('department7_id')],
        ["department8_name", this.getDepartmentName('department8_id')],
        ["department9_name", this.getDepartmentName('department9_id')],
        ["department10_name", this.getDepartmentName('department10_id')],
        ["printer_name", this.getPrinterName],
        ["terminal_name", this.getTerminalName],
    ])


    items = new Map<string, Function>([
        ["sku", this.getSku],
        ["item_barcodes", this.getBarcodeList],
        ["category_name", this.getCategoryId],
        ["department_name", this.getDepartmentId('department_id', 'department')],
        ["department2_name", this.getDepartmentId('department2_id', 'department2')],
        ["department3_name", this.getDepartmentId('department3_id', 'department3')],
        ["department4_name", this.getDepartmentId('department4_id', 'department4')],
        ["department5_name", this.getDepartmentId('department5_id', 'department5')],
        ["department6_name", this.getDepartmentId('department6_id', 'department6')],
        ["department7_name", this.getDepartmentId('department7_id', 'department7')],
        ["department8_name", this.getDepartmentId('department8_id', 'department8')],
        ["department9_name", this.getDepartmentId('department9_id', 'department9')],
        ["department10_name", this.getDepartmentId('department10_id', 'department10')],
    ])


    private getMovementRowAmount = (params: any) => {
        const { price, quantity, perc_discount } = params.data ?? {};
        if (price == null) return null;
        return Math.round(price * quantity * (1 - ((perc_discount || 0) / 100)) * 100) / 100;
    }

    stock_movements = new Map<string, Function>([
        ["movement_type", this.getMovementType]
    ])

    stock_movements_search = new Map<string, Function>([
        ["row_amount", this.getMovementRowAmount],
        ["movement_type", this.getMovementType]
    ])

    stock_movements_detail = new Map<string, Function>([
        ["row_amount", this.getMovementRowAmount]
    ])
}